import React, { useContext, useEffect } from 'react'
import { NotificationManagerContext, Text, View } from 'oio-react'
import pluralize from 'pluralize'
import Helmet from 'react-helmet'
import { Link } from 'react-router-dom'
import { ThreadUpdateGlyph24Icon } from 'assets/icons'
import { LAYOUT_BOX, LAYOUT_HEADER } from 'config/constants/ui'
import { initiativeUrl } from 'config/constants/urls'
import Title from 'src/apps/CustomApp/components/Title'
import AnnouncementBanner from 'src/custom/gns3/AnnouncementBanner'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import { useInitiativeList, useMe, useMyDirectThreadList, useMyNotificationCount,
   useOrganization } from 'src/graphql/hooks'
import InboxListItem from '~/components/InboxListItem'
import InvitationListItem from '~/components/InvitationListItem'

const MessagesInbox = () => {
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const { showNotification } = useContext(NotificationManagerContext)

   const { notificationCount } = useMyNotificationCount()
   const { organization } = useOrganization()
   const isBoxGL = theme.tmpGlobalLayout === LAYOUT_BOX
   const isHeaderGL = theme.tmpGlobalLayout === LAYOUT_HEADER

   const { me, refetch: refetchMe } = useMe()
   const { myDirectThreadList, refetch: refetchMyDirectThreadList } = useMyDirectThreadList()
   const { initiativeList: initiativeListWithUnreadMessages, refetch: refetchInitiativeList } =
      useInitiativeList({
         userRelationshipFilter: {
            userId: 'me',
            hasUnreadMessages: true
         }
      })

   // If Notification Count changes, refetch all data that appears in Inbox
   useEffect(() => {
      refetchMe()
      refetchMyDirectThreadList()
      refetchInitiativeList()
   }, [notificationCount])

   return (
      <View
         width="100%"
         height="100%"
         paddingRight={(isHeaderGL || isBoxGL) && `
               ${theme.tmpViewportPaddingC}[c]
               ${theme.tmpViewportPaddingD}[d]
               ${theme.tmpViewportPaddingE}[e]
               ${theme.tmpViewportPaddingF}[f]
            `
         }
         scroll="on">
         <View
            width="100%"
            minHeight="100%"
            paddingHorizontal={`
               ${theme.tmpViewportPaddingA}[a]
               ${theme.tmpViewportPaddingB}[b]
               30px[c-d]
               40px[e-f]`
            }
            backgroundColor={theme.tmpContentBackgroundColor}>
            <Helmet title={`Inbox | ${organization.name}`} />
            <View
               flex="0 0 auto"
               display="flex"
               justifyContent="space-between"
               width="100%"
               paddingVertical={theme.tmpSpacingTop}
               backgroundColor={theme.tmpContentBackgroundColor}
               borderBottom={`1px solid ${theme.tmpContentLineColor}`}>
               <Title size="md" color={theme.tmpContentTitleColor}>Inbox</Title>
            </View>
            {/* TODO: Get this abomination out of here
            This is a temporary solution to silence MB for the time being
            See PR #518 */}
            {organization.slug === 'gns3' && <AnnouncementBanner />}
            {!notificationCount && (
               <View
                  width="100%"
                  padding="60px"
                  textAlign="center">
                  <Text size="2" color="#aaa">
                     Your inbox is currently empty
                  </Text>
               </View>
            )}
            {me.invitations.map((relationship) => {
               const invitedRoleParticipantElements = relationship.elements.filter(element => (
                  element.__typename === 'RelationshipElementRoleParticipant' &&
                  element.status === 'invited'
               ))

               return invitedRoleParticipantElements.map(element => (
                  <InvitationListItem
                     key={element.id}
                     onAccept={() => {
                        refetchMe()
                        showNotification({
                           message: 'Thank you for accepting this invitation',
                           title: 'Success!',
                           type: 'success'
                        })
                     }}
                     onDecline={refetchMe}
                     relationship={relationship}
                     roleParticipantElement={element}
                  />
               ))
            })}
            {myDirectThreadList.items.filter(t => t.hasUnreadEntries).map(thread => (
               <InboxListItem
                  key={thread.id}
                  date={thread.lastEntryDate}
                  description={(
                     <>
                        <Link
                           to={`/messages/direct/${thread.id}`}
                           style={{ fontWeight: '500', color: '#444' }}>
                           {thread.title}
                        </Link>
                        {` sent you ${thread.numUnreadEntries} ${pluralize('message', thread.numUnreadEntries)}`}
                     </>
                  )}
                  icon={<ThreadUpdateGlyph24Icon width="18px" height="18px" color="#fff" />}
                  iconBackgroundColor="#64c642"
               />
            ))}
            {/* This is currently designed with the assumption that notifications are related
            to thread updates only
            See PR #394 */}
            {initiativeListWithUnreadMessages.items.map(initiative => (
               <InboxListItem
                  key={initiative.id}
                  date={initiative.thread.lastEntryDate}
                  description={(
                     <>
                        {initiative.addedBy.id === me.id
                           ? `Your ${initiative.type.nameSingular}: `
                           : `The ${initiative.type.nameSingular} you are following: `
                        }
                        <Link
                           to={initiativeUrl(initiative)}
                           style={{ fontWeight: '500', color: '#444' }}>
                           {initiative.name}
                        </Link>
                        {` has ${initiative.thread.numUnreadEntries} new ${pluralize('comment', initiative.thread.numUnreadEntries)}`}
                     </>
                  )}
                  icon={<ThreadUpdateGlyph24Icon width="18px" height="18px" color="#fff" />}
                  iconBackgroundColor="#45abff"
               />
            ))}
         </View>
      </View>
   )
}

export default MessagesInbox
